import React from 'react';
import { RiStackLine, RiTeamLine, RiFlashlightLine, RiErrorWarningLine, RiFlowChart } from "react-icons/ri";
import WorkflowBuilderDiagram from './diagrams/WorkflowBuilderDiagram';

const WorkflowsContent = () => {
  const isDarkMode = document.body.classList.contains('dark-mode');
  const iconStyle = { verticalAlign: "middle", marginRight: "8px" };

  return (
    <div style={{ marginTop: "1rem", lineHeight: "1.6", fontSize: "0.95rem" }}>
      <h2 style={{ borderBottom: "1px solid var(--panel-border, #ccc)", paddingBottom: "0.5rem" }}>
        Workflows
      </h2>
      <p>
        Stackform has three modes. Choose based on what you need — a
        synthesized answer, a live multi-agent debate, or a custom
        multi-step pipeline you define and reuse.
      </p>

      {/* ── ASK ─────────────────────────────────────────────────── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <h3 style={{ color: "var(--primary-color, #636cff)", display: "flex", alignItems: "center" }}>
          <RiStackLine size={24} style={iconStyle} />
          Orchestration — Ask Mode
        </h3>
        <p>
          The default intelligent research workflow. The Orchestrator acts as
          an expert conductor: it answers immediately from its own knowledge,
          dispatches the right specialists in parallel, and synthesizes
          everything into a final comprehensive report.
        </p>

        <div style={{ paddingLeft: '0.5rem', borderLeft: '3px solid var(--primary-color, #636cff)', marginTop: '1rem' }}>
          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            Agents
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <li>
              <strong>Orchestrator:</strong> Plans, routes, and synthesizes. Delivers the
              preliminary answer and the final report.
            </li>
            <li>
              <strong>Generalist:</strong> Real-time web search, current events, news,
              and general knowledge queries.
            </li>
            <li>
              <strong>Researcher:</strong> Academic paper search and evidence-based
              analysis from scientific literature.
            </li>
            <li>
              <strong>Evaluator:</strong> Independent auditor. Assesses accuracy,
              completeness, and reasoning quality of any response.
            </li>
            <li>
              <strong>Challenger:</strong> Constructive critic. Stress-tests claims,
              exposes hidden assumptions, and surfaces the strongest counter-argument
              to any position. Assign to any Swarm agent or workflow node to inject
              adversarial scrutiny.
            </li>
          </ul>

          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            Live Controls
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <li>
              <strong>Stop Thinking:</strong> Stop background research at any time and keep
              the current preliminary answer. Useful when the first response is already sufficient.
            </li>
          </ul>

          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            On-Demand Response Actions
          </strong>
          <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Available on any completed response — trigger at any time:
          </p>
          <ul style={{ marginTop: '0.25rem', marginBottom: '1rem' }}>
            <li>
              <strong>Concise:</strong> Shows the Orchestrator's fast, direct preliminary
              answer without any sub-agent research.
            </li>
            <li>
              <strong>Expansive:</strong> Re-runs the full agentic deep-dive from scratch
              to generate the most thorough multi-source answer possible.
            </li>
            <li>
              <strong>Evaluation:</strong> Sends the response to the Evaluator agent for
              a structured critique — covering accuracy, depth, and any gaps or unsupported claims.
            </li>
          </ul>
        </div>

        <p style={{ marginTop: '1rem' }}>
          <em>
            Best for: research questions, complex factual queries, document
            analysis, and anything where accuracy and depth are paramount.
          </em>
        </p>
      </section>

      {/* ── SWARM ────────────────────────────────────────────────── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <h3 style={{ color: "var(--success-color, #28a745)", display: "flex", alignItems: "center" }}>
          <RiTeamLine size={24} style={iconStyle} />
          Swarm — Open Forum
        </h3>
        <p>
          Initiates a dynamic, real-time debate between multiple AI agents.
          Unlike Ask mode, Swarm keeps you in the loop as an active participant —
          you can intervene, steer, pause, and shape the discussion as it unfolds.
        </p>

        <div style={{ paddingLeft: '0.5rem', borderLeft: '3px solid var(--success-color, #28a745)', marginTop: '1rem' }}>
          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            Controls
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <li>
              <strong>Turns:</strong> Set the maximum number of agent contributions before
              the swarm stops automatically.
            </li>
            <li>
              <strong>Intervene:</strong> Inject new context, ask a follow-up question, or
              redirect the topic mid-debate. Agents incorporate your input in their next turn.
            </li>
            <li>
              <strong>Pause / Resume:</strong> Suspend the debate at any point to read and
              reflect, then resume when ready.
            </li>
            <li>
              <strong>Pause on Turn:</strong> Enable this toggle to have the swarm stop
              automatically after every single agent contribution, giving you full turn-by-turn
              control.
            </li>
            <li>
              <strong>Delay:</strong> Adjust the pacing between agent turns for more
              comfortable reading.
            </li>
            <li>
              <strong>Summarize:</strong> At any point (after at least one agent has spoken),
              request a conclusive report that synthesizes the entire debate into a structured
              final answer.
            </li>
          </ul>
        </div>

        <p style={{ marginTop: '1rem' }}>
          <em>
            Best for: brainstorming, exploring conflicting perspectives, creative
            writing, ethical dilemmas, and open-ended discovery where the
            conversation itself is the value.
          </em>
        </p>
      </section>

      {/* ── WORKFLOW PIPELINES ───────────────────────────────────── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <h3 style={{ color: "#f59e0b", display: "flex", alignItems: "center" }}>
          <RiFlowChart size={24} style={iconStyle} />
          Workflows — Visual Pipelines
        </h3>
        <p>
          Build reusable, multi-step AI pipelines on a drag-and-drop canvas,
          then run them with a single click from the chat panel. Workflows are
          ideal for any process you want to define once and repeat reliably —
          structured research briefs, multi-stage analysis, routing logic,
          tool-augmented reasoning, and more.
        </p>

        <WorkflowBuilderDiagram />

        <div style={{ paddingLeft: '0.5rem', borderLeft: '3px solid #f59e0b', marginTop: '1rem' }}>
          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            Node Types
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <li><strong>Input:</strong> Receives the user's query and passes it downstream.</li>
            <li><strong>Agent:</strong> Runs a specialist (Generalist, Researcher, Evaluator, Challenger, or any custom agent) on the current context.</li>
            <li><strong>Tool:</strong> Calls an external tool or lookup (web search, API, etc.).</li>
            <li><strong>Logic:</strong> Branches, merges, or sets variables — e.g. route by a confidence score, or combine the outputs of parallel nodes.</li>
            <li><strong>Output:</strong> Collects and formats the final result for display.</li>
          </ul>

          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            Built-in Platform Workflows
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <li><strong>Tool to Agent:</strong> Runs a tool lookup first, then passes the result to an agent for synthesis.</li>
            <li><strong>Agent Only:</strong> Single-agent pipeline — direct, focused answer from one specialist.</li>
            <li><strong>Route by Score:</strong> Evaluates confidence and routes to a deeper agent when the initial score is low.</li>
            <li><strong>Research Brief:</strong> Full research pipeline — web search, academic literature, synthesis — delivered as a structured brief.</li>
            <li><strong>Parallel Perspectives:</strong> Two agents argue both sides of a topic in parallel, then a third synthesizes a balanced conclusion.</li>
          </ul>

          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            Running a Workflow
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <li>Select a workflow from the <strong>⚙ Workflows</strong> dropdown in the mode selector.</li>
            <li>Type your input and press <strong>Run</strong>.</li>
            <li>
              Runs execute asynchronously and stream back live to the chat
              panel. A <strong>run card</strong> shows each node's status as it
              works — including <strong>live per-agent progress</strong> inside a
              node (each subagent starting and finishing, plus the synthesis
              step). You can close the panel and return; the run keeps going
              server-side.
            </li>
            <li>Open the <strong>Workflow Builder</strong> (via the Workflows menu) to create, edit, or duplicate custom pipelines.</li>
          </ul>
        </div>

        <p style={{ marginTop: '1rem' }}>
          <em>
            Best for: recurring research tasks, structured analysis pipelines,
            any multi-step process you want to define once and reuse reliably.
          </em>
        </p>
      </section>

      {/* ── CONCURRENT SESSIONS ──────────────────────────────────── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <h3 style={{ color: "var(--text-color)", display: "flex", alignItems: "center" }}>
          <RiFlashlightLine size={24} style={iconStyle} />
          Concurrent Sessions
        </h3>
        <p>
          Stackform lets you run multiple Ask and Swarm sessions at the same
          time. Each session is independent — you can start a new Ask query
          while a Swarm debate is still in progress, or queue up several
          research queries back-to-back without waiting for any of them to
          finish first.
        </p>

        <div style={{ paddingLeft: '0.5rem', borderLeft: '3px solid var(--primary-color, #636cff)', marginTop: '1rem', marginBottom: '1rem' }}>
          <strong style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-secondary, #666)', letterSpacing: '0.04em' }}>
            How it works
          </strong>
          <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            <li>
              <strong>Ask sessions</strong> run fully in the background — you can
              close the browser and come back; the concise and expansive answers
              will be waiting for you. The sidebar shows a spinner on any
              conversation that is still in progress.
            </li>
            <li>
              <strong>Swarm sessions</strong> keep running server-side whether
              or not your browser is open. Reconnecting to a running session
              replays all past turns and continues streaming live events.
            </li>
            <li>
              Use <strong>Stop Thinking</strong> (Ask) or <strong>Stop</strong>{" "}
              (Swarm) at any time to end a session early and preserve what has
              already been generated.
            </li>
          </ul>
        </div>

        <div style={{
          display: 'flex',
          gap: '0.75rem',
          backgroundColor: isDarkMode ? '#2d2310' : '#fffbeb',
          border: `1px solid ${isDarkMode ? '#b45309' : '#f59e0b'}`,
          borderRadius: '8px',
          padding: '0.85rem 1rem',
          marginTop: '1.5rem',
          marginBottom: '1.5rem',
        }}>
          <RiErrorWarningLine size={20} style={{ color: isDarkMode ? '#fbbf24' : '#b45309', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '0.9rem' }}>
            <strong style={{ color: isDarkMode ? '#fbbf24' : '#b45309' }}>Provider limits advisory</strong>
            <p style={{ margin: '0.35rem 0 0' }}>
              Every concurrent session draws on your configured provider
              simultaneously. Running many sessions at once can quickly exhaust
              your provider's <strong>requests-per-minute (RPM)</strong>,{" "}
              <strong>tokens-per-minute (TPM)</strong>, or daily quota,
              causing throttle errors or failed requests across all active
              sessions. Check your provider dashboard and keep the number of
              simultaneous sessions within your plan's limits.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default WorkflowsContent;
